import { useDroppable } from '@dnd-kit/core';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Box, styled, Typography } from '@mui/material';

const PREFIX = 'TSMED-DeleteZoneOverlay';

const deleteZoneClasses = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  icon: `${PREFIX}-icon`,
  label: `${PREFIX}-label`,
} as const;

const Root = styled(Box, {
  shouldForwardProp: prop => !['$isOver', '$visible'].includes(prop as string),
})<{ $isOver: boolean; $visible: boolean }>(({ $isOver, $visible, theme }) => ({
  [`&.${deleteZoneClasses.root}`]: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99998,
    pointerEvents: $visible ? 'all' : 'none',
    opacity: $visible ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: theme.spacing(6),
  },

  [`& .${deleteZoneClasses.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 6),
    borderRadius: theme.spacing(2),
    background: $isOver
      ? 'linear-gradient(to top, rgba(120, 120, 120, 0.95) 0%, rgba(100, 100, 100, 0.7) 50%, rgba(80, 80, 80, 0.3) 100%)'
      : 'linear-gradient(to top, rgba(60, 60, 60, 0.8) 0%, rgba(50, 50, 50, 0.5) 50%, rgba(40, 40, 40, 0.2) 100%)',
    border: `2px dashed ${$isOver ? '#fff' : 'rgba(255, 255, 255, 0.3)'}`,
    transition: 'all 0.3s ease-in-out',
    transform: $isOver ? 'scale(1.1)' : 'scale(1)',
    boxShadow: $isOver
      ? `0 0 50px rgba(140, 140, 140, 0.9), 0 0 90px rgba(140, 140, 140, 0.5)`
      : '0 4px 20px rgba(0, 0, 0, 0.5)',
  },

  [`& .${deleteZoneClasses.icon}`]: {
    fontSize: '48px',
    color: '#fff',
    transition: 'transform 0.3s ease-in-out',
    transform: $isOver ? 'scale(1.2) rotate(10deg)' : 'scale(1)',
  },

  [`& .${deleteZoneClasses.label}`]: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 600,
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    letterSpacing: '0.5px',
  },
}));

interface DeleteZoneOverlayProps {
  visible: boolean;
  onDelete?: () => void;
}

export const DeleteZoneOverlay = ({
  visible,
  onDelete,
}: DeleteZoneOverlayProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'delete-zone',
  });

  const handleClick = (e: React.MouseEvent) => {
    if (visible && onDelete) {
      e.stopPropagation();
      onDelete();
    }
  };

  return (
    <Root
      ref={setNodeRef}
      className={deleteZoneClasses.root}
      $isOver={isOver}
      $visible={visible}
      onClick={handleClick}
    >
      <Box className={deleteZoneClasses.content}>
        <DeleteIcon className={deleteZoneClasses.icon} />
        <Typography className={deleteZoneClasses.label}>
          {isOver ? 'Release to Delete' : 'Drop here to delete'}
        </Typography>
      </Box>
    </Root>
  );
};
